import React, { useState, useCallback, useMemo, memo, useEffect } from "react";
import { X } from "lucide-react";
import StatusStep from "./StatusStep";
import SubitemsTable from "./SubitemsTable";
import { SubitemsService } from "../services/SubitemsService";
import { StatusValidationService } from "../services/statusValidationService";
import StatusAdvancementWarningModal from "./StatusAdvancementWarningModal";
import ModelBuilderAssignmentModal from "./ModelBuilderAssignmentModal";
import CorrectionsReasonModal from "./CorrectionsReasonModal";
import { useAuth } from "../contexts/AuthContext";
import { KPIService } from "../services/kpiService";
import { formatJobRevNumber } from "../utils/projectFormatting";
import "./FullSubitemModal.css";
import { useTrackModal } from "../hooks/useTrackModal";

const FullSubitemModal = memo(
  ({ project, isOpen, onClose, onUpdateProject, employees }) => {
    useTrackModal("FullSubitemModal", isOpen);
    // ALL HOOKS MUST BE CALLED FIRST - before any conditional logic
    const { user } = useAuth();
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [subitems, setSubitems] = useState([]);
    const [subitemsLoading, setSubitemsLoading] = useState(false);
    const [subitemsError, setSubitemsError] = useState(null);
    
    // Status validation warning modal state
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [pendingStatusChange, setPendingStatusChange] = useState(null);
    const [incompleteSubitems, setIncompleteSubitems] = useState([]);
    const [bypassValidation, setBypassValidation] = useState(false);

    // KPI modal state
    const [showBuilderAssignmentModal, setShowBuilderAssignmentModal] = useState(false);
    const [showCorrectionsReasonModal, setShowCorrectionsReasonModal] = useState(false);
    const [pendingKPIStatusChange, setPendingKPIStatusChange] = useState(null);

    // Memoize the modeling status workflow to prevent recreation on every render
    const modelingStatuses = useMemo(
      () => [
        "Startup",
        "Data Prep",
        "Utilities",
        "Ready to Model",
        "Modeling",
        "Double Check",
        "Corrections",
        "Review",
        "Ready to Export",
        "Completed",
      ],
      [],
    );

    const currentStatusIndex = useMemo(
      () => (project ? modelingStatuses.indexOf(project.modelingStatus) : -1),
      [modelingStatuses, project],
    );

    const handleStatusClick = useCallback(
      async (newStatus, statusIndex) => {
        console.log("🎯 ProjectModal: handleStatusClick called", {
          newStatus,
          statusIndex,
          currentStatusIndex,
          projectStatus: project?.modelingStatus,
          updatingStatus,
          bypassValidation
        });

        if (!project || updatingStatus) {
          console.log("❌ ProjectModal: Blocked - no project or updating");
          return;
        }
        
        // Allow clicking any status (past, current, or future) to move to that stage
        if (statusIndex === currentStatusIndex) {
          console.log("❌ ProjectModal: Blocked - already at this status");
          return; // Don't update if already at this status
        }

        // === KPI INTERCEPTS ===

        // Intercept: Modeling → Double Check - require model builder assignment
        if (project.modelingStatus === "Modeling" && newStatus === "Double Check") {
          if (!project.modelBuilderId) {
            console.log("🔍 KPI: No model builder assigned, showing assignment modal");
            setPendingKPIStatusChange({ newStatus, statusIndex });
            setShowBuilderAssignmentModal(true);
            return;
          }
        }

        // Intercept: Review → Corrections - require reason
        if (project.modelingStatus === "Review" && newStatus === "Corrections") {
          if (!project.modelBuilderId) {
            console.log("❌ KPI: No model builder assigned for corrections tracking");
            alert("A model builder must be assigned before sending to Corrections.");
            return;
          }
          console.log("🔍 KPI: Showing corrections reason modal");
          setPendingKPIStatusChange({ newStatus, statusIndex });
          setShowCorrectionsReasonModal(true);
          return;
        }

        // === END KPI INTERCEPTS ===

        setUpdatingStatus(true);
        try {
          console.log("🔍 ProjectModal: Validating status change...", {
            projectId: project.id,
            currentStatus: project.modelingStatus,
            targetStatus: newStatus,
            bypassValidation,
            currentStatusIndex,
            targetStatusIndex: statusIndex
          });

          // Check if we should bypass validation (after override)
          if (bypassValidation) {
            console.log("🔄 ProjectModal: Bypassing validation after override");
            setBypassValidation(false); // Reset the bypass flag
            await onUpdateProject(project.id, "modelingStatus", newStatus);
            setUpdatingStatus(false);
            return;
          }

          // Validate the status change
          const validation = await StatusValidationService.validateStatusChange(
            project.id,
            project.modelingStatus,
            newStatus
          );

          console.log("🔍 ProjectModal: Validation result:", validation);

          if (!validation.isValid && validation.incompleteSubitems.length > 0) {
            // Show warning modal with incomplete subitems
            setIncompleteSubitems(validation.incompleteSubitems);
            setPendingStatusChange({ newStatus, statusIndex });
            setShowWarningModal(true);
            setUpdatingStatus(false);
            return;
          }

          // Validation passed or no incomplete subitems, proceed with update
          await onUpdateProject(project.id, "modelingStatus", newStatus);
        } catch (error) {
          console.error("Failed to update status:", error);
        } finally {
          setUpdatingStatus(false);
        }
      },
      [
        currentStatusIndex,
        onUpdateProject,
        project,
        updatingStatus,
        bypassValidation,
      ],
    );

    const getStatusClass = useCallback(
      (statusIndex) => {
        if (statusIndex < currentStatusIndex) return "status-completed";
        if (statusIndex === currentStatusIndex) return "status-current";
        return "status-future";
      },
      [currentStatusIndex],
    );

    // Warning modal handlers
    const handleCloseWarning = useCallback(() => {
      setShowWarningModal(false);
      setPendingStatusChange(null);
      setIncompleteSubitems([]);
      setUpdatingStatus(false);
    }, []);

    const handleOverrideWarning = useCallback(async () => {
      if (!pendingStatusChange || !project) {
        console.log("🔍 ProjectModal: Override blocked - no pending change or project");
        return;
      }

      // Store values before clearing state
      const targetStatus = pendingStatusChange.newStatus;
      const currentIncompleteItems = incompleteSubitems;
      const currentPendingChange = pendingStatusChange;

      console.log("🔥 ProjectModal: ===== OVERRIDE STARTING =====");
      console.log("🔍 ProjectModal: Overriding warning, proceeding with status change", {
        from: project.modelingStatus,
        to: targetStatus,
        currentStatusIndex: modelingStatuses.indexOf(project.modelingStatus),
        targetStatusIndex: modelingStatuses.indexOf(targetStatus)
      });
      
      // Clear modal state FIRST to prevent re-triggering
      setShowWarningModal(false);
      setIncompleteSubitems([]);
      setPendingStatusChange(null);
      setUpdatingStatus(true);
      
      try {
        // Set global flag to bypass App.js validation since we've already handled override
        // This prevents dual validation between ProjectModal and App.js when user clicks Override
        window.__statusOverrideApproved = true;
        await onUpdateProject(project.id, "modelingStatus", targetStatus);
        // Clear the global flag after a short delay
        setTimeout(() => {
          window.__statusOverrideApproved = false;
        }, 500);
        
        // Set bypass flag to prevent re-validation on next interaction
        setBypassValidation(true);
        console.log("🔄 ProjectModal: Setting bypass validation flag");
        
        // Clear the bypass flag after a short delay to prevent permanent bypass
        setTimeout(() => {
          console.log("🔄 ProjectModal: Clearing bypass validation flag");
          setBypassValidation(false);
        }, 1000);
      } catch (error) {
        console.error("❌ ProjectModal: Failed to update status after override:", error);
        // Re-show the warning modal if the update failed
        setShowWarningModal(true);
        setIncompleteSubitems(currentIncompleteItems);
        setPendingStatusChange(currentPendingChange);
      } finally {
        setUpdatingStatus(false);
      }
    }, [pendingStatusChange, project, onUpdateProject, incompleteSubitems, modelingStatuses]);

    // Handle re-validation when items are marked complete in warning modal
    const handleRevalidate = useCallback(async () => {
      if (!project || !pendingStatusChange) return;

      console.log("🔄 ProjectModal: Re-validating after subitem updates");

      try {
        // Re-run validation
        const validation = await StatusValidationService.validateStatusChange(
          project.id,
          project.modelingStatus,
          pendingStatusChange.newStatus
        );

        console.log("🔍 ProjectModal: Re-validation result:", validation);

        // Update the incomplete subitems with the new validation result
        setIncompleteSubitems(validation.incompleteSubitems);

        // If validation now passes, close modal and proceed with status update
        if (validation.isValid || validation.incompleteSubitems.length === 0) {
          console.log("🎉 ProjectModal: All items completed, proceeding with status change");
          setShowWarningModal(false);
          setUpdatingStatus(true);
          
          try {
            await onUpdateProject(project.id, "modelingStatus", pendingStatusChange.newStatus);
          } catch (error) {
            console.error("Failed to update status after re-validation:", error);
          } finally {
            setPendingStatusChange(null);
            setIncompleteSubitems([]);
            setUpdatingStatus(false);
          }
        }
      } catch (error) {
        console.error("Failed to re-validate status change:", error);
      }
    }, [project, pendingStatusChange, onUpdateProject]);

    // === KPI MODAL HANDLERS ===

    const handleBuilderAssignmentSubmit = useCallback(async (builderId) => {
      if (!project || !pendingKPIStatusChange) return;
      try {
        await KPIService.assignModelBuilder(project.id, builderId);
        setShowBuilderAssignmentModal(false);
        setUpdatingStatus(true);
        await onUpdateProject(project.id, "modelingStatus", pendingKPIStatusChange.newStatus);
        setPendingKPIStatusChange(null);
      } catch (error) {
        console.error("Failed to assign model builder:", error);
      } finally {
        setUpdatingStatus(false);
      }
    }, [project, pendingKPIStatusChange, onUpdateProject]);

    const handleCorrectionsReasonSubmit = useCallback(async () => {
      if (!project || !pendingKPIStatusChange) return;
      try {
        setShowCorrectionsReasonModal(false);
        setUpdatingStatus(true);
        await onUpdateProject(project.id, "modelingStatus", pendingKPIStatusChange.newStatus);
        setPendingKPIStatusChange(null);
      } catch (error) {
        console.error("Failed to update status after corrections reason:", error);
      } finally {
        setUpdatingStatus(false);
      }
    }, [project, pendingKPIStatusChange, onUpdateProject]);

    const handleCloseBuilderAssignmentModal = useCallback(() => {
      setShowBuilderAssignmentModal(false);
      setPendingKPIStatusChange(null);
    }, []);

    const handleCloseCorrectionsReasonModal = useCallback(() => {
      setShowCorrectionsReasonModal(false);
      setPendingKPIStatusChange(null);
    }, []);

    const modelBuilderName = useMemo(() => {
      if (!project?.modelBuilderId || !employees) return null;
      const builder = employees.find((e) => e.id === project.modelBuilderId);
      return builder?.name || null;
    }, [project?.modelBuilderId, employees]);

    // === END KPI MODAL HANDLERS ===

    // Fetch subitems when project changes or status changes
    const fetchSubitems = useCallback(async () => {
      if (!project || !project.id) return;

      setSubitemsLoading(true);
      setSubitemsError(null);

      try {
        const items = await SubitemsService.getSubitemsByStatus(
          project.id,
          project.modelingStatus,
        );
        setSubitems(items);
      } catch (error) {
        console.error("Error loading subitems:", error);
        setSubitemsError(error.message);
        setSubitems([]);
      } finally {
        setSubitemsLoading(false);
      }
    }, [project]);

    useEffect(() => {
      if (isOpen) {
        fetchSubitems();
      }
    }, [isOpen, fetchSubitems]);

    // Handle subitem updates
    const handleSubitemUpdate = useCallback((updatedSubitem) => {
      setSubitems((prev) =>
        prev.map((item) =>
          item.id === updatedSubitem.id ? updatedSubitem : item,
        ),
      );
    }, []);

    // Memoize the click handler to prevent modal from closing accidentally
    const handleModalClick = useCallback((e) => {
      e.stopPropagation();
    }, []);

    const handleOverlayClick = useCallback(
      (e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      },
      [onClose],
    );

    // CONDITIONAL RENDERING AFTER ALL HOOKS
    if (!isOpen || !project) {
      return null;
    }

    return (
      <div className="modal-overlay" onClick={handleOverlayClick}>
        <div
          className="project-modal"
          onClick={handleModalClick}
          ref={(node) => {
            if (node) {
              node.style.setProperty('width', '88%', 'important');
              node.style.setProperty('max-width', '2400px', 'important');
              node.style.setProperty('min-width', '0', 'important');
            }
          }}
        >
          {/* Header - Standardized */}
          <div
            style={{
              padding: 'var(--space-6)',
              borderBottom: '1px solid var(--gray-700)',
              background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
              borderTopLeftRadius: 'var(--radius-xl)',
              borderTopRightRadius: 'var(--radius-xl)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
              {/* Job Number and Project Name on one line */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}>
                <span
                  style={{
                    fontSize: 'var(--text-2xl)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-400)',
                    flexShrink: 0,
                  }}
                >
                  {formatJobRevNumber(project.jobNumber, project.rev)}
                </span>
                <h1
                  style={{
                    margin: 0,
                    fontSize: 'var(--text-2xl)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-50)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {project.projectName}
                </h1>
              </div>


              {/* Close Button */}
              <button
                onClick={onClose}
                aria-label="Close modal"
                style={{
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  borderRadius: '6px',
                  color: 'var(--gray-400)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'var(--gray-700)';
                  e.target.style.color = 'var(--gray-200)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'var(--gray-400)';
                }}
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Status Progress Bar */}
          <div className="status-progress-section">
            <h3>Project Progress</h3>
            <div className="status-progress-bar">
              {modelingStatuses.map((status, index) => (
                <StatusStep
                  key={status}
                  status={status}
                  index={index}
                  currentStatusIndex={currentStatusIndex}
                  updatingStatus={updatingStatus}
                  onStatusClick={handleStatusClick}
                  getStatusClass={getStatusClass}
                />
              ))}
            </div>
          </div>

          {/* Subitems Table */}
          <div className="subitems-section">
            <SubitemsTable
              subitems={subitems}
              loading={subitemsLoading}
              error={subitemsError}
              onSubitemUpdate={handleSubitemUpdate}
              employees={employees}
              modelingStatus={project.modelingStatus}
              projectId={project.id}
            />
          </div>
        </div>

        {/* Status Advancement Warning Modal */}
        <StatusAdvancementWarningModal
          isOpen={showWarningModal}
          onClose={handleCloseWarning}
          onOverride={handleOverrideWarning}
          incompleteSubitems={incompleteSubitems}
          currentStatus={project?.modelingStatus}
          targetStatus={pendingStatusChange?.newStatus}
          projectId={project?.id}
          employees={employees}
          onSubitemUpdate={handleSubitemUpdate}
          onRevalidate={handleRevalidate}
        />

        {/* Model Builder Assignment Modal (KPI) */}
        <ModelBuilderAssignmentModal
          isOpen={showBuilderAssignmentModal}
          onClose={handleCloseBuilderAssignmentModal}
          onSubmit={handleBuilderAssignmentSubmit}
          projectName={project?.projectName}
          jobNumber={formatJobRevNumber(project?.jobNumber, project?.rev)}
        />

        {/* Corrections Reason Modal (KPI) */}
        <CorrectionsReasonModal
          isOpen={showCorrectionsReasonModal}
          onClose={handleCloseCorrectionsReasonModal}
          onSubmit={handleCorrectionsReasonSubmit}
          projectId={project?.id}
          projectName={project?.projectName}
          jobNumber={formatJobRevNumber(project?.jobNumber, project?.rev)}
          modelBuilderId={project?.modelBuilderId}
          modelBuilderName={modelBuilderName}
          userEmail={user?.email}
          userId={user?.id}
        />
      </div>
    );
  },
);

FullSubitemModal.displayName = "FullSubitemModal";

export default FullSubitemModal;
